import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import moment from 'moment';
import { ToastContainer, toast } from 'react-toastify';

import Tabs from './Tabs';
import BranchDropdown from './BranchDropdown';
import MonthDropdown, { MONTH_OPTIONS } from './MonthDropdown';
import ProposalTable from './ProposalTable';
import DphRFQDetails from './DphRFQDetails';
import ActionLoader from 'Components/ActionLoader/index';
import { ReactComponent as BackIcon } from 'Icons/leftback.svg';
import {
    GET_DPH_APPROVER_DETAILS,
    GET_DPH_BRANCHES_LIST,
    GET_DPH_DASHBOARD_LIST,
    GET_DPH_RFQ_DETAILS,
    GET_DPH_DOWNLOAD_EXCEL,
    POST_DPH_APPROVE_REJECT_PROPOSAL,
    POST_DPH_SEND_TO_APPROVAL,
    DOWNLOAD_UPLOADED_PRESIGNED_FILE,
} from 'Constants/actionConstants';
import './Dph.scss';


const DphDashboard = () => {
    const dispatch = useDispatch();
    const history = useHistory();
    const loader = useSelector(state => state.contracting.loader);

    const [currentView, setCurrentView] = useState('dashboard');
    const [tab, setTab] = useState(0);
    const [dphDataList, setDphDataList] = useState([]);
    const [dphRfqDetails, setDphRfqDetails] = useState({});
    const [dphApproverDetails, setDphApproverDetails] = useState({});
    console.log("dphApproverDetails", dphApproverDetails)

    const DPH_TABS = useMemo(() => {
        const tabs = [];
        if (dphApproverDetails?.is_approver == false) {
            tabs.push({ label: 'New DPH Proposal', value: 0 });
        }
        tabs.push(
            { label: 'Pending for Approval', value: 1 },
            { label: 'Approved DPH', value: 2 },
            { label: 'Rejected DPH', value: 3 },
        );
        return tabs;
    }, [dphApproverDetails?.is_approver]);
    const [dphDataBranchList, setDphDataBranchList] = useState([]);
    const [selectedBranch, setSelectedBranch] = useState(null);
    const [selectedMonth, setSelectedMonth] = useState(MONTH_OPTIONS[moment().add(1, 'month').month()]);
    const [selectedRows, setSelectedRows] = useState(null);
    const [branchDocuments, setBranchDocuments] = useState({});
    const [proposedValues, setProposedValues] = useState({});
    const [dashboardReloadTrigger, setDashboardReloadTrigger] = useState(0);
    const rfqBranchIdRef = useRef(null);
    const awaitingApproverRef = useRef(false);

    const fromDate = useMemo(
        () => moment().month(selectedMonth.value).startOf('month').valueOf(),
        [selectedMonth.value]
    );
    const toDate = useMemo(
        () => moment().month(selectedMonth.value).endOf('month').set({ hour: 23, minute: 59, second: 59, millisecond: 0 }).valueOf(),
        [selectedMonth.value]
    );
    const nextMonthLabel = useMemo(() => moment().add(1, 'month').format('MMMM, YYYY'), []);
    const previousMonthLabel = useMemo(() => moment().subtract(1, 'month').format('Do MMMM, YYYY'), []);

    const getDphDashBoardData = useCallback(() => {
        if (awaitingApproverRef.current) return;
        const params = {
            dphFromDate: fromDate,
            dphToDate: toDate,
            dashBoardStatus: tab,
        };

        if (selectedBranch) {
            params.branchId = selectedBranch.value;
            params.branchName = selectedBranch.label;
        }

        dispatch({
            type: GET_DPH_DASHBOARD_LIST,
            params,
            onSuccess: (data) => {
                setDphDataList(data);
                const docs = {};
                data.forEach(item => {
                    if (item.dph_document) {
                        const fileName = item.dph_document.split('/').pop()?.split('?')[0] || 'document.pdf';
                        docs[item.branch_id] = [{ file_name: fileName, downloadUrl: item.dph_document, fromApi: true }];
                    }
                });
                setBranchDocuments(docs);
            },
        });
    }, [dispatch, fromDate, toDate, tab, selectedBranch, dashboardReloadTrigger]);

    const getDphDashRfqDetails = useCallback((branchId, page = 1, size = 10, search = '') => {
        dispatch({
            type: GET_DPH_RFQ_DETAILS,
            params: { branchId, page, size, search },
            onSuccess: setDphRfqDetails,
        });
    }, [dispatch]);

    const getDphApproverDetails = useCallback((branchId) => {
        awaitingApproverRef.current = true;
        dispatch({
            type: GET_DPH_APPROVER_DETAILS,
            params: { branchId },
            onSuccess: (data) => {
                awaitingApproverRef.current = false;
                setDphApproverDetails(data);
                setTab(data?.is_approver == true ? 1 : 0);
                setDashboardReloadTrigger(t => t + 1);
            },
        });
    }, [dispatch]);

    const getDphDashboardBranchList = useCallback(() => {
        dispatch({
            type: GET_DPH_BRANCHES_LIST,
            params: { dphFromDate: fromDate, dphToDate: toDate },
            onSuccess: (res) => {
                const branchOptions = res?.map(item => ({
                    label: item.branch_name,
                    value: item.branch_id,
                })) || [];
                setDphDataBranchList(branchOptions);
                if (branchOptions.length > 0) {
                    setSelectedBranch(prev => prev ?? branchOptions[0]);
                    getDphApproverDetails(branchOptions[0].value);
                }
            },
        });
    }, [dispatch, fromDate, toDate, getDphApproverDetails]);

    const toggleRow = useCallback((branchId) => {
        setSelectedRows(prev => (prev === branchId ? null : branchId));
    }, []);

    const handleTabChange = useCallback((newTab) => {
        setTab(newTab);
        setSelectedRows(null);
    }, []);

    const refreshAndReset = useCallback(() => {
        getDphDashBoardData();
        setSelectedRows(null);
    }, [getDphDashBoardData]);

    const handleProposedChange = useCallback((branchId, value) => {
        setProposedValues(prev => ({ ...prev, [branchId]: value }));
    }, []);

    const handleDocumentChange = useCallback((branchId, documents) => {
        setBranchDocuments(prev => ({ ...prev, [branchId]: documents }));
    }, []);

    const handleClickSendToApproval = useCallback((item, remarks) => {
        const documents = branchDocuments[item.branch_id] || [];

        if (!documents.length) {
            toast.error('Please upload DPH document before sending for approval.');
            return;
        }

        const proposed_diesel_price = proposedValues[item.branch_id] ?? item.proposed_diesel_price ?? item.current_diesel_price;

        const submit = (dph_document) => {
            dispatch({
                type: POST_DPH_SEND_TO_APPROVAL,
                data: {
                    dphFromDate: fromDate,
                    dphToDate: toDate,
                    branch_id: item.branch_id,
                    branch_name: item.branch_name,
                    previous_diesel_price: item.previous_diesel_price,
                    current_diesel_price: item.current_diesel_price,
                    proposed_diesel_price,
                    dph_document,
                    remarks,
                },
                onSuccess: refreshAndReset,
            });
        };

        const document = documents[0];
        if (document.downloadUrl) {
            submit(document.downloadUrl);
            return;
        }

        dispatch({
            type: DOWNLOAD_UPLOADED_PRESIGNED_FILE,
            param: { fileName: document.file_name },
            skipDownload: true,
            onSuccess: (url) => {
                if (!url) {
                    toast.error('Unable to prepare uploaded document. Please try again.');
                    return;
                }
                submit(url);
            },
        });
    }, [branchDocuments, proposedValues, dispatch, fromDate, toDate, refreshAndReset]);

    const handleClickApprove = useCallback((item, remarks) => {
        dispatch({
            type: POST_DPH_APPROVE_REJECT_PROPOSAL,
            data: {
                branch_id: item.branch_id,
                company_id: item.company_id,
                is_approve: true,
                remarks,
            },
            onSuccess: refreshAndReset,
        });
    }, [dispatch, refreshAndReset]);

    const handleClickReject = useCallback((item, remarks) => {
        dispatch({
            type: POST_DPH_APPROVE_REJECT_PROPOSAL,
            data: {
                branch_id: item.branch_id,
                company_id: item.company_id,
                is_reject: true,
                remarks,
            },
            onSuccess: refreshAndReset,
        });
    }, [dispatch, refreshAndReset]);

    const handleDownloadExcel = useCallback((item) => {
        dispatch({
            type: GET_DPH_DOWNLOAD_EXCEL,
            params: {
                branchId: item.branch_id,
                branchName: item.branch_name,
                dphFromDate: fromDate,
                dphToDate: toDate,
                proposedDieselPrice: item.proposed_diesel_price,
            },
            onSuccess: (url) => {
                if (url) {
                    const a = document.createElement('a');
                    a.href = url;
                    a.download = url.split('/').pop().split('?')[0] || 'dph_excel.xlsx';
                    a.click();
                }
            },
        });
    }, [dispatch, fromDate, toDate]);

    const handleBack = useCallback(() => history.goBack(), [history]);

    const handleDphDashboardBack = useCallback(() => setCurrentView('dashboard'), []);

    const handleRfqPageChange = useCallback((page, search) => {
        getDphDashRfqDetails(rfqBranchIdRef.current, page, 10, search);
    }, [getDphDashRfqDetails]);

    const handleViewRfq = useCallback((rfqRow) => {
        rfqBranchIdRef.current = rfqRow?.branch_id;
        setCurrentView('rfq');
        getDphDashRfqDetails(rfqRow?.branch_id, 1);
    }, [getDphDashRfqDetails]);

    useEffect(() => {
        if (dphApproverDetails?.is_approver == true) {
            setTab(1);
        } else if (dphApproverDetails?.is_approver == false) {
            setTab(0);
        }
    }, [dphApproverDetails?.is_approver]);

    useEffect(() => {
        getDphDashboardBranchList();
    }, [getDphDashboardBranchList]);

    useEffect(() => {
        if (selectedBranch?.value) {
            getDphApproverDetails(selectedBranch.value);
        }
    }, [selectedBranch?.value]);

    useEffect(() => {
        getDphDashBoardData();
    }, [getDphDashBoardData]);

    return (
        <div className="dph-dashboard">
            <ToastContainer />
            {loader && <ActionLoader fixed={true} />}

            {currentView === 'dashboard' ? (
                <>
                    <div className="flex fs-22 dark-blue mr-30 curP" onClick={handleBack}>
                        <BackIcon className="ml-5 curP mr-3" />
                        <span className="fSemibold pt-8">Back</span>
                    </div>

                    <h1>DPH Dashboard</h1>

                    <Tabs tabs={DPH_TABS} active={tab} onChange={handleTabChange} />

                    <div className="filter-row">
                        {tab !== 0 && <MonthDropdown
                            value={selectedMonth}
                            onChange={setSelectedMonth}
                        />}
                        <BranchDropdown
                            options={dphDataBranchList}
                            value={selectedBranch}
                            onChange={setSelectedBranch}
                        />
                    </div>

                    <div className="proposal-header">
                        <h2>Propose DPH for <span>{nextMonthLabel}</span></h2>
                        <p>New Proposal as on {previousMonthLabel}</p>
                    </div>

                    <ProposalTable
                        dphDataList={dphDataList}
                        toggleRow={toggleRow}
                        selectedRows={selectedRows}
                        handleClickSendToApproval={handleClickSendToApproval}
                        onViewRfq={handleViewRfq}
                        handleClickApprove={handleClickApprove}
                        handleClickReject={handleClickReject}
                        tab={tab}
                        branchDocuments={branchDocuments}
                        onDocumentChange={handleDocumentChange}
                        proposedValues={proposedValues}
                        handleProposedChange={handleProposedChange}
                        isDownloadExcelEnabled={dphApproverDetails?.is_download_excel_enable}
                        onDownloadExcel={handleDownloadExcel}
                    />
                </>
            ) : (
                <DphRFQDetails
                    rfqList={dphRfqDetails?.list}
                    totalCount={dphRfqDetails?.total_count}
                    onBack={handleDphDashboardBack}
                    onPageChange={handleRfqPageChange}
                />
            )}
        </div>
    );
};

export default DphDashboard;
